import { IonIcon, IonItem, useIonRouter } from "@ionic/react";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  briefcaseOutline,
  closeOutline,
  ellipsisHorizontal,
  locationOutline,
  timeOutline,
} from "ionicons/icons";
import { toast } from "sonner";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import {
  APPLICATION_STATUS_DISPLAY,
  UNKNOWN_STATUS_DISPLAY,
} from "@/lib/application/status-display";
import { api } from "@/lib/backend";
import { type Application } from "@/lib/backend/api";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { useRevokePeriod } from "@/lib/backend/utils/hooks/use-revoke-period";
import { queryClient } from "@/lib/providers/query";
import { Routes } from "@/lib/router";
import { formatDateBox, formatTimeRange } from "@/utils/format-datetime";
import { getSlotRoleLabel } from "@/utils/functions/get-slot-role-label";

export function UpcomingApplicationCardOverview({
  application,
  hasMatchingShift,
}: {
  application: Application;
  hasMatchingShift?: boolean;
}) {
  const { t } = useTranslation();
  const router = useIonRouter();
  const modalRevoke = useRef<HTMLIonModalElement>(null);
  const [openPopover, setOpenPopover] = React.useState(false);

  const { canRevokeApplication } = useRevokePeriod();

  const revokeApplicationMutation = useExtractedMutation({
    mutationFn: api.application.revokeEmployeeApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["applications"],
      });
      toast.success(t("common:applications.revoked"));
    },
    onError: () => toast.error(t("actions:revoke.error")),
  });

  // Projecttijd. `tzDate()` leek dit al te regelen, maar levert een Date waarvan
  // de LOKALE velden de Amsterdamse tijd voorstellen — en tempo's `format()` legt
  // daar de tijdzone van het toestel nóg eens overheen. Op een telefoon in
  // Toronto werd 15:00 daardoor 03:00.
  const timeZone = application.availabilitySlot?.project?.timeZone;
  const dateBox = formatDateBox(application.startAt, { timeZone });

  const slot = application.availabilitySlot;
  const projectName = slot?.project?.name || "Project";
  // Position name (e.g. "Bardienst") wins over the generic function name (#373).
  const userFunctionName = getSlotRoleLabel(slot, "Functie");
  const addressLine = slot?.project?.addressLine;

  const canRevoke = canRevokeApplication(application.startAt);

  // Een status die de api later toevoegt heeft hier geen regel: dan blijft de ruwe waarde
  // staan, net als in de oude `default:`-tak.
  const statusDisplay = APPLICATION_STATUS_DISPLAY[application.status];
  const statusLabel = statusDisplay ? t(statusDisplay.labelKey) : application.status;
  const statusIcon = statusDisplay?.icon ?? UNKNOWN_STATUS_DISPLAY.icon;
  const statusColor = statusDisplay?.color ?? UNKNOWN_STATUS_DISPLAY.color;

  const handleCardClick = (e: React.MouseEvent) => {
    // Navigate to project detail without affecting tab history
    router.push(
      Routes.Project({ id: slot?.project?.id }, { search: { fromUrl: Routes.UpcomingShifts() } }),
      "forward",
      "push",
    );
  };

  const handleRevoke = async () => {
    try {
      await revokeApplicationMutation.mutateAsync({
        id: application.id,
      });
      modalRevoke.current?.dismiss();
    } catch (error) {
      toast.error(t("actions:revoke.error"));
    }
  };

  const isCancelled = application.status === "CANCELLED";

  return (
    <>
      <div
        onClick={handleCardClick}
        className={`relative flex w-full cursor-pointer gap-5 ${isCancelled ? "opacity-60" : ""}`}
      >
        {/* Date box */}
        <div className="flex w-12 shrink-0 flex-col items-center px-1 py-2">
          <div className="flex flex-col items-center pb-0.5 text-center text-txt-primary">
            <p className="text-sm font-semibold leading-tight">{dateBox.month}</p>
            <p className="text-2xl font-semibold leading-none">{dateBox.day}</p>
          </div>
          <p className="text-sm font-medium text-txt-tertiary">{dateBox.caption}</p>
        </div>

        {/* Application details */}
        <div className="flex flex-1 items-start gap-5 border-b border-bordercolor-primary pb-5">
          <div className="flex flex-1 flex-col gap-3 py-2">
            {/* Title and type label */}
            <div className="flex flex-col">
              <p className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
                AANMELDING
              </p>
              <h3
                className={`overflow-hidden text-ellipsis text-lg font-semibold leading-[1.6] text-txt-primary ${isCancelled ? "line-through" : ""}`}
              >
                {projectName}
              </h3>
            </div>

            {/* Metadata */}
            <div className="flex flex-col gap-2">
              {/* Function */}
              <div className="flex items-center gap-1.5">
                <IonIcon icon={briefcaseOutline} className="text-xl text-txt-tertiary" />
                <p className="text-base font-medium text-txt-tertiary">{userFunctionName}</p>
              </div>

              {/* Location */}
              {addressLine && (
                <div className="flex items-center gap-1.5">
                  <IonIcon icon={locationOutline} className="text-xl text-txt-tertiary" />
                  <p className="text-base font-medium text-txt-tertiary">{addressLine}</p>
                </div>
              )}

              {/* Time */}
              <div className="flex items-center gap-1.5">
                <IonIcon icon={timeOutline} className="text-base text-txt-tertiary" />
                <p className="text-base font-medium text-txt-tertiary">
                  {formatTimeRange(application.startAt, application.endAt, { timeZone })}
                </p>
              </div>

              {/* Status */}
              <div className="flex items-center justify-start gap-1.5">
                <IonIcon icon={statusIcon} className={`text-base ${statusColor}`} />
                <p className={`text-base font-medium ${statusColor}`}>{statusLabel}</p>
              </div>
            </div>
          </div>

          {/* Three dots menu - only show if application can be revoked */}
          {canRevoke && (application.status === "PENDING" || application.status === "ACCEPTED") && (
            <div className="relative flex shrink-0 items-start py-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenPopover(!openPopover);
                }}
                className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-surface-secondary"
              >
                <IonIcon icon={ellipsisHorizontal} className="text-2xl text-txt-primary" />
              </button>

              {/* Popover */}
              {openPopover && (
                <>
                  {/* Backdrop to close popover when clicking outside */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenPopover(false);
                    }}
                  />

                  {/* Popover content */}
                  <div className="absolute right-0 top-12 z-20 w-max rounded-xl border border-bordercolor-primary bg-white px-5 py-3.5 shadow-[0px_20px_33px_8px_rgba(0,0,0,0.08),0px_10px_18px_8px_rgba(0,0,0,0.02)]">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenPopover(false);
                        setTimeout(() => {
                          modalRevoke.current?.present();
                        }, 100);
                      }}
                      className="flex items-center justify-between gap-4 whitespace-nowrap"
                    >
                      <span className="text-txt-error text-base font-medium">
                        Aanmelding intrekken
                      </span>
                      <IonIcon icon={closeOutline} className="text-txt-error text-xl" />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Confirmation Modal */}
      <ModalSheetAutoHeight ref={modalRevoke}>
        <div className="flex flex-col gap-6 p-6">
          {/* Header */}
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-txt-primary">Aanmelding intrekken</h2>
          </div>

          {/* Content */}
          <p className="text-sm leading-relaxed text-txt-secondary">
            Weet je zeker dat je je aanmelding wilt intrekken? Deze actie kan niet ongedaan worden
            gemaakt.
          </p>

          {/* Action button */}
          <button
            type="button"
            onClick={handleRevoke}
            disabled={revokeApplicationMutation.isPending}
            className="w-full rounded-lg bg-error-100 py-3 text-center text-base font-semibold text-white disabled:opacity-50"
          >
            {revokeApplicationMutation.isPending
              ? "Intrekken..."
              : t("common:applications.revoke-confirm")}
          </button>
        </div>
      </ModalSheetAutoHeight>
    </>
  );
}
